import { inputFieldsComplete } from "./checklistInputFields";

export const CHECKLIST_RESPONSE_ACTION_KEYS = [
  "performed",
  "approved",
  "na",
  "deviation",
];

/** Default for existing types/items with no explicit configuration (API + UI). */
export const LEGACY_DEFAULT_ACTION_KEYS = ["approved", "deviation"];

const ALLOWED = new Set(CHECKLIST_RESPONSE_ACTION_KEYS);

export function normalizeActionKeys(value) {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return [...LEGACY_DEFAULT_ACTION_KEYS];
  }
  const out = [...new Set(value.filter((k) => ALLOWED.has(String(k))))];
  return out.length ? out : [...LEGACY_DEFAULT_ACTION_KEYS];
}

/** Checklist type-level defaults (array of keys). */
export function effectiveTypeDefaultActions(checklistType) {
  return normalizeActionKeys(checklistType?.default_response_actions);
}

/**
 * Template row: null/empty `response_actions` means inherit from checklist type defaults.
 */
export function effectiveActionsForTemplateRow(row, typeDefaultKeys) {
  const base = normalizeActionKeys(typeDefaultKeys);
  if (
    row?.response_actions != null &&
    Array.isArray(row.response_actions) &&
    row.response_actions.length > 0
  ) {
    return normalizeActionKeys(row.response_actions);
  }
  return base;
}

/** Instance checklist item: backend stores resolved list; fallback to type. */
export function effectiveActionsForChecklistItem(item, checklistType) {
  if (
    item?.response_actions != null &&
    Array.isArray(item.response_actions) &&
    item.response_actions.length > 0
  ) {
    return normalizeActionKeys(item.response_actions);
  }
  return effectiveTypeDefaultActions(checklistType);
}

const OUTCOME_CODES = new Set([
  "performed",
  "approved",
  "na",
  "deviation",
]);

export function itemRowResolved(item) {
  if (
    item?.isApproved ||
    item?.isDeviation ||
    item?.is_not_applicable
  ) {
    return true;
  }
  const code = item?.response_code;
  return code != null && OUTCOME_CODES.has(String(code));
}

/** «Ikke aktuelt» — API flag `is_not_applicable` or outcome key `na`. */
export function itemIsNotApplicable(item) {
  return (
    Boolean(item?.is_not_applicable) || String(item?.response_code || "") === "na"
  );
}

/** Explicit avvik only — not N/A, not unanswered, not performed/approved. */
export function itemIsDeviation(item) {
  return (
    Boolean(item?.isDeviation) || String(item?.response_code || "") === "deviation"
  );
}

/**
 * Display/serialization key. Mirrors ChecklistItem#pdf_outcome_key:
 * na → deviation → performed → approved → pending.
 */
export function itemOutcomeKey(item) {
  if (itemIsNotApplicable(item)) return "na";
  if (itemIsDeviation(item)) return "deviation";
  if (item?.isApproved && String(item?.response_code || "") === "performed") {
    return "performed";
  }
  if (item?.isApproved) return "approved";
  const code = String(item?.response_code || "");
  if (code === "performed" || code === "approved") return code;
  return "pending";
}

/** Documents-required gap. N/A is complete and does not block the mangler bar. */
export function itemHasDocumentsGap(item) {
  if (!item?.documents_required) return false;
  if (itemIsNotApplicable(item)) return false;
  const docs = item?.documents;
  return !Array.isArray(docs) || docs.length === 0;
}

/**
 * Remaining gaps for the sticky overview — same completion idea as
 * Checklist#is_checked / itemRowResolved (unanswered + missing required docs).
 * N/A counts as handled. Only explicit avvik is a deviation, never a “fail” for N/A.
 */
export function collectChecklistGaps(items) {
  const list = Array.isArray(items) ? items.filter((row) => row && !row.delete) : [];
  const unanswered = [];
  const documents = [];
  const fields = [];

  list.forEach((item) => {
    if (!itemRowResolved(item)) {
      unanswered.push(item);
      return;
    }
    if (itemHasDocumentsGap(item)) {
      documents.push(item);
    } else if (!inputFieldsComplete(item.input_fields, item.input_values, itemOutcomeKey(item))) {
      fields.push(item);
    }
  });

  const gaps = [...unanswered, ...documents, ...fields];
  return {
    unanswered,
    documents,
    fields,
    gaps,
    resolvedCount: list.length - gaps.length,
    totalCount: list.length,
    deviationCount: list.filter((item) => itemIsDeviation(item)).length,
  };
}

export function toggleKeyInList(list, key) {
  const k = String(key);
  if (!ALLOWED.has(k)) return list;
  const set = new Set(list);
  if (set.has(k)) set.delete(k);
  else set.add(k);
  const next = CHECKLIST_RESPONSE_ACTION_KEYS.filter((x) => set.has(x));
  return next.length ? next : [...LEGACY_DEFAULT_ACTION_KEYS];
}

export function listsEqualAsSets(a, b) {
  const na = normalizeActionKeys(a);
  const nb = normalizeActionKeys(b);
  if (na.length !== nb.length) return false;
  const sa = new Set(na);
  return nb.every((x) => sa.has(x));
}
